import { GapValues } from "@/lib/utils/GapValues";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { DateTextInput } from "@stenajs-webui/calendar";
import { Column, Indent, Row, Text } from "@stenajs-webui/core";
import { FlatButton, Label, PrimaryButton } from "@stenajs-webui/elements";
import { Popover } from "@stenajs-webui/tooltip";
import * as React from "react";
import { useState } from "react";
import { otherStretches, parentSpanError } from "../lib/parentDateRules";
import { ParentHistoryEntry } from "../types";

interface Props {
  entry: ParentHistoryEntry;
  history: ParentHistoryEntry[];
  /** Settles once the write and the read after it are done. */
  onSetEnd: (validTo: string) => Promise<unknown>;
}

/**
 * The end of a stretch, and the only date on it that moves.
 *
 * Where a parent starts is decided once, when it is added, and stands.
 * Moving a start would change which days a parent applied to without any
 * say over what took its place, so the end is the only handle offered.
 *
 * Every stretch can have its end moved, whichever side of today it falls -
 * correcting when a parent stopped applying is a legitimate thing to want.
 * Moved, though, not removed: once a stretch has an end there is no way
 * back to it running on.
 * The date is picked inside a popover that will not let it be saved while
 * it clashes with another parent, and says which one is in the way.
 */
export const ParentHistoryEndDateCell: React.FC<Props> = ({
  entry,
  history,
  onSetEnd,
}) => {
  const [value, setValue] = useState(entry.validTo ?? "");
  /** Kept outside the popover's render so a save survives a re-render. */
  const [saving, setSaving] = useState(false);

  /**
   * There is no way to take an end date away again - the API has no
   * operation for it - so an empty field is a dead end rather than a way
   * of running the stretch on.
   */
  const error = !value
    ? "An end date can be moved but not removed."
    : parentSpanError(otherStretches(history, entry.validFrom), {
        validFrom: entry.validFrom,
        validTo: value,
      });

  return (
    <Popover
      placement={"bottom-start"}
      trigger={"click"}
      zIndex={ZIndex.highest}
      onClosed={() => setValue(entry.validTo ?? "")}
      renderTrigger={(props) => (
        <Indent>
          <FlatButton {...props} label={entry.validTo ?? "Set end date"} />
        </Indent>
      )}
    >
      {({ onRequestClose }) => (
        <Column gap={GapValues.SIXTEEN} width={"320px"}>
          <Label text={"Last day under this parent"}>
            <DateTextInput
              value={value}
              onValueChange={(next) => setValue(next ?? "")}
              portalTarget={document.body}
              zIndex={ZIndex.highest}
            />
          </Label>

          {error && <Text color={"var(--lhds-color-red-500)"}>{error}</Text>}

          <Row gap={GapValues.SIXTEEN} justifyContent={"flex-end"}>
            <FlatButton
              label={"Cancel"}
              disabled={saving}
              onClick={onRequestClose}
            />
            {/*
              The popover stays open for the write, so the spinner has
              somewhere to sit, and shuts once the table behind it already
              shows the new date.
            */}
            <PrimaryButton
              label={"Save"}
              loading={saving}
              loadingLabel={"Saving"}
              disabled={!!error || value === entry.validTo}
              onClick={async () => {
                setSaving(true);
                await onSetEnd(value);
                setSaving(false);
                onRequestClose();
              }}
            />
          </Row>
        </Column>
      )}
    </Popover>
  );
};
